import { useCallback } from 'react';

interface IGetLtvHighLimitParams {
  isShopByCallbackMode: boolean;
  sbcLtv?: number | null;
  // Vehicle Search only (SWA-7940). Selecting a tier seeds Max LTV with that tier's F/E LTV Limit,
  // so a value equal to it is a default on display rather than a limit the user asked for. Passing
  // the tier's limit lets that default stand aside for a term's O'ride FE LTV; leaving it out keeps
  // the previous behaviour, where any value in the field is sent as-is.
  tierLtvLimit?: number | null;
  // The LTV the deal source approved, which autofills Max LTV. Withheld like the tier default and
  // sent separately, so the backend can cap the deal by the lower of it and the admin limit.
  dealSourceLtv?: number | null;
}

export const useFormOverrideLtv = (getValues: () => any) => {
  const hasFormOverrideLtv = useCallback(() => {
    const val = getValues()?.preferences?.overrideLtv;

    return val != null && String(val) !== '' && Number.isFinite(Number(val));
  }, [getValues]);

  const getFormOverrideLtv = useCallback(() => Number(getValues()?.preferences?.overrideLtv), [getValues]);

  const getLtvHighLimit = useCallback(
    ({ isShopByCallbackMode, sbcLtv, tierLtvLimit, dealSourceLtv }: IGetLtvHighLimitParams) => {
      if (isShopByCallbackMode) return sbcLtv;

      if (hasFormOverrideLtv()) {
        const overrideLtv = getFormOverrideLtv();
        const isSeededTierDefault = tierLtvLimit != null && !dealSourceLtv && overrideLtv === Number(tierLtvLimit);
        const isDealSourceAutofill = dealSourceLtv != null && overrideLtv === Number(dealSourceLtv);

        // 0 tells the backend no LTV was asked for, so it applies the term's O'ride FE LTV (or the
        // tier limit) and caps that by any deal_source_ltv_high_limit sent alongside.
        return isSeededTierDefault || isDealSourceAutofill ? 0 : overrideLtv;
      }

      return Number(getValues()?.preferences?.ltvHighLimit) || 0;
    },
    [hasFormOverrideLtv, getFormOverrideLtv, getValues],
  );

  return { hasFormOverrideLtv, getFormOverrideLtv, getLtvHighLimit };
};
